/**
 * iter63c — RoleSwitcher (top-nav dropdown)
 *
 * Compact one-click role switcher. Shows when the authed user holds more
 * than one role on the current tenant (drawn from ``GET /auth/my-roles``).
 *
 * Behaviour:
 *   - Hidden entirely for guests / single-role users (no UI noise).
 *   - Click → opens a small panel listing the OTHER roles (excluding the
 *     one currently in session).
 *   - Selecting a role → calls ``POST /auth/switch-role`` (which now
 *     rotates both the JWT cookie + the legacy Bearer token), then
 *     navigates to that console's default landing page.
 *
 * The customer ProfilePage already exposes a more verbose "My Consoles"
 * card. This component is for **fast switching from anywhere** in the
 * UI without having to navigate back to Profile.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { ChevronDown, Repeat, ShoppingBag, Store, Truck, Shield, MapPin } from "lucide-react";
import { toast } from "sonner";

import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { useTenant } from "../contexts/TenantContext";
import { STAFF_ROLES, staffCustomerViewAllowed } from "../lib/staffCustomerView";
import { consoleInBuild } from "../lib/buildLock";

const CONSOLE_BY_ROLE = {
    CUSTOMER: { label: "Customer", to: "/", Icon: ShoppingBag },
    RESTAURANT_OWNER: { label: "Merchant", to: "/merchant", Icon: Store },
    RIDER: { label: "Rider", to: "/rider", Icon: Truck },
    TENANT_ADMIN: { label: "Admin", to: "/admin", Icon: Shield },
    ZONE_ADMIN: { label: "Zone admin", to: "/zone-admin", Icon: MapPin },
    SUPER_ADMIN: { label: "Super admin", to: "/super", Icon: Shield },
};

export default function RoleSwitcher({ className = "", dropDirection = "down" }) {
    const { user, tenant } = useAuth();
    const { tenant: liveTenant } = useTenant();
    const [roles, setRoles] = useState([]);
    const [open, setOpen] = useState(false);
    const [busy, setBusy] = useState(false);
    const wrapRef = useRef(null);

    // Pull roles for the active tenant once on mount + whenever the user
    // changes (e.g. after a successful switch). Guests get nothing.
    useEffect(() => {
        if (!user) { setRoles([]); return; }
        let cancelled = false;
        api.get("/auth/my-roles")
            .then(({ data }) => { if (!cancelled) setRoles(data?.roles || []); })
            .catch(() => { /* silent — not a critical UI */ });
        return () => { cancelled = true; };
    }, [user]);

    // Close on outside click.
    useEffect(() => {
        if (!open) return;
        const handler = (e) => {
            if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, [open]);

    const customerViewForStaff = staffCustomerViewAllowed(liveTenant);
    const otherRoles = useMemo(() => {
        const seen = new Set();
        // Admin → Branding → "Show Customer View to Shop Owners and Admins". When OFF, shop owners and admins aren't
        // offered the storefront (OwnerConsoleRedirect would only send them back). ON — the default — changes nothing.
        const hideCustomer = !customerViewForStaff && (roles || []).some((r) => STAFF_ROLES.has(r.role));
        return (roles || []).filter((r) => {
            if (hideCustomer && r.role === "CUSTOMER") return false;
            // Skip the role currently in session.
            if (r.role === user?.role && r.tenant_id === user?.tenant_id) return false;
            // Skip unknown roles (defensive — keeps the dropdown stable
            // if the backend adds a role the frontend doesn't yet
            // recognise).
            if (!CONSOLE_BY_ROLE[r.role]) return false;
            // iter181 — never offer a console this build doesn't ship (the rider app has only the rider screens:
            // switching there moved the phone's notifications to an account whose pages don't exist in the app).
            if (!consoleInBuild(r.role)) return false;
            const key = `${r.role}|${r.tenant_id || "_"}`;
            if (seen.has(key)) return false;
            seen.add(key);
            return true;
        });
    }, [roles, user, customerViewForStaff]);

    // Hide entirely for guests + single-role users — no UI noise.
    if (!user || otherRoles.length === 0) return null;

    const currentCfg = CONSOLE_BY_ROLE[user.role] || { label: user.role, Icon: Repeat };
    const CurrentIcon = currentCfg.Icon;

    const switchTo = async (target) => {
        if (busy) return;
        setBusy(true);
        try {
            await api.post("/auth/switch-role", {
                role: target.role,
                tenant_slug: target.tenant_slug || undefined,
            });
            // iter66 — Hard-navigate instead of setState + soft nav.
            //
            // Background: the previous implementation called
            // ``login(token, user, tenant)`` followed by ``nav(cfg.to)``.
            // Even with React 18 auto-batching, the source layout's
            // ``useEffect`` guard (e.g. AdminLayout redirects to /login
            // when ``user.role`` isn't TENANT_ADMIN) could fire during
            // the transient render where ``user`` had already flipped
            // to the new role but the URL hadn't yet remounted the
            // destination layout. The result was an immediate bounce
            // to /login after every cross-role switch.
            //
            // Hard navigation sidesteps the race entirely: the server
            // has already set the new httpOnly cookies, so the next
            // page load re-initialises AuthContext from a clean slate
            // with the correct role. No transient state, no flicker.
            toast.success(`Switched to ${target.role.replaceAll("_", " ").toLowerCase()}`);
            const cfg = CONSOLE_BY_ROLE[target.role];
            window.location.assign(cfg?.to || "/");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Switch failed");
            setBusy(false);
        }
    };

    return (
        <div ref={wrapRef} className={`relative ${className}`}>
            <button
                data-testid="role-switcher-btn"
                onClick={() => setOpen((v) => !v)}
                className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full bg-muted hover:bg-muted/80 text-xs font-semibold border border-border transition"
                title={`You're in ${currentCfg.label} view. Click to switch.`}
            >
                <CurrentIcon size={13} />
                <span className="hidden sm:inline">{currentCfg.label}</span>
                <ChevronDown size={12} className={`transition ${open ? "rotate-180" : ""}`} />
            </button>
            {open && (
                <div
                    data-testid="role-switcher-menu"
                    className={`absolute z-40 right-0 w-[220px] bg-card border border-border rounded-xl shadow-xl overflow-hidden ${dropDirection === "up" ? "bottom-full mb-1.5" : "mt-1.5"}`}
                >
                    <div className="px-3 py-2 text-[10px] uppercase tracking-wider font-bold text-muted-foreground border-b border-border bg-muted/40">
                        Switch view · {tenant?.name || ""}
                    </div>
                    <div className="max-h-[260px] overflow-y-auto">
                    {otherRoles.map((r) => {
                        const cfg = CONSOLE_BY_ROLE[r.role];
                        const RIcon = cfg.Icon;
                        return (
                            <button
                                key={`${r.role}-${r.tenant_id || ""}`}
                                data-testid={`role-switcher-to-${r.role}`}
                                onClick={() => switchTo(r)}
                                disabled={busy}
                                className="w-full text-left px-3 py-2.5 hover:bg-muted transition flex items-center gap-3 disabled:opacity-60"
                            >
                                <div className="w-8 h-8 rounded-lg bg-primary/10 text-primary flex items-center justify-center flex-shrink-0">
                                    <RIcon size={14} />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="text-sm font-semibold truncate">{cfg.label}</div>
                                    {r.first_name && (
                                        <div className="text-[11px] text-muted-foreground truncate">{r.first_name}</div>
                                    )}
                                </div>
                            </button>
                        );
                    })}
                    </div>
                </div>
            )}
        </div>
    );
}
